import { useWorkspaceStore } from '@/store/workspace';

// import { notifyTokenRefreshFailed, notifyTokenRefreshSuccess } from './token-refresh-events';
type FetcherOptions = RequestInit & {
  baseUrl?: string;
  simulateDelay?: boolean; // Tùy chọn để mô phỏng delay
  delayMs?: number; // Thời gian delay (mặc định 5000ms)
  skipTokenRefresh?: boolean; // Skip token refresh for this request
};

// API Error type to preserve error response structure
export class ApiError extends Error {
  public status: string;
  public error_code?: string;
  public statusCode: number;

  constructor(message: string, status: string, error_code?: string, statusCode?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.error_code = error_code;
    this.statusCode = statusCode || 400;
  }
}

// Token refresh state management
// let isRefreshing = false;
// let refreshPromise: Promise<boolean> | null = null;

// /**
//  * Refresh token function
//  */
// async function refreshToken(): Promise<boolean> {
//   try {
//     console.log('🔄 Attempting to refresh token...');

//     // Use direct fetch to avoid circular dependency
//     const response = await fetch('/api/platform/auth/v1/auth/refresh', {
//       method: 'POST',
//       credentials: 'include',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//     });

//     if (response.ok) {
//       const data = await response.json();
//       if (data.status === 'OK') {
//         console.log('✅ Token refreshed successfully');
//         notifyTokenRefreshSuccess();
//         return true;
//       }
//     }

//     console.log('❌ Token refresh failed:', response.status);
//     notifyTokenRefreshFailed();
//     return false;
//   } catch (error) {
//     console.error('❌ Token refresh error:', error);
//     notifyTokenRefreshFailed();
//     return false;
//   }
// }

/**
 * Handle token refresh with singleton pattern
 */
// async function handleTokenRefresh(): Promise<boolean> {
//   if (isRefreshing && refreshPromise) {
//     // If already refreshing, wait for the existing promise
//     return refreshPromise;
//   }

//   isRefreshing = true;
//   refreshPromise = refreshToken();

//   try {
//     const success = await refreshPromise;
//     return success;
//   } finally {
//     isRefreshing = false;
//     refreshPromise = null;
//   }
// }

/**
 * Redirect to login page
 */
// function redirectToLogin(): void {
//   if (typeof window !== 'undefined') {
//     // Clear any auth data before redirecting
//     try {
//       sessionStorage.clear();
//       localStorage.clear();
//     } catch (error) {
//       console.error('Error clearing storage:', error);
//     }

//     // Redirect to login
//     window.location.href = '/login';
//   }
// }

/**
 * Hàm helper để delay (chờ) một khoảng thời gian
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * A wrapper around fetch that handles common functionality
 * - Automatically parses JSON responses
 * - Handles errors consistently
 * - Supports absolute and relative URLs
 * - Handles 401 errors with automatic token refresh
 */
export async function fetcher<T>(url: string, options: FetcherOptions = {}): Promise<T> {
  const {
    simulateDelay = false,
    delayMs = 5000,
    headers: optionsHeaders,
    // skipTokenRefresh = false,
    ...fetchOptions
  } = options;

  // Get selected workspace from store
  const selectedWorkspace = useWorkspaceStore.getState().selectedWorkspace;

  // Set default headers if not provided
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(optionsHeaders as Record<string, string>),
  };

  // Add workspace headers to ALL requests if workspace is selected
  if (selectedWorkspace) {
    if (!headers['X-Workspace-Id'] && !headers['x-workspace-id']) {
      headers['X-Workspace-Id'] = selectedWorkspace.workspace_id;
    }
    if (!headers['X-Organization-Id'] && !headers['x-organization-id']) {
      headers['X-Organization-Id'] = selectedWorkspace.organization_id;
    }
  }

  // Internal fetch function
  const performFetch = async (): Promise<T> => {
    // Mô phỏng delay nếu được yêu cầu
    if (simulateDelay) {
      await delay(delayMs);
    }

    const finalOptions = {
      ...fetchOptions,
      headers,
      credentials: 'include' as RequestCredentials,
    };

    const response = await fetch(url, finalOptions);

    // Parse the JSON response
    const data = await response.json();

    // Handle API error responses
    if (!response.ok || data.status === 'INVALID' || data.status === 'ERROR') {
      // Handle UNAUTHORIZED - redirect to login
      if (data.status === 'UNAUTHORIZED') {
        // Check if we're not already on a public/auth page to avoid redirect loops
        const publicPaths = [
          '/login',
          '/signup',
          '/auth',
          '/callback',
          '/login-callback',
          '/error',
          '/403',
          '/unauthorized',
        ];
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

        // Also check if we're in the middle of an OAuth flow
        const urlParams =
          typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
        const isOAuthCallback = urlParams && (urlParams.has('code') || urlParams.has('state'));

        // Check if user is currently logging out - skip redirect if so
        let isLoggingOut = false;
        if (typeof window !== 'undefined') {
          const { useUserStore } = await import('@/store/user/store');
          isLoggingOut = useUserStore.getState().isLoggingOut ?? false;
        }

        if (
          !isLoggingOut &&
          !publicPaths.some(path => currentPath.startsWith(path)) &&
          !isOAuthCallback &&
          !currentPath.includes('login-callback')
        ) {
          console.warn('[Fetcher] UNAUTHORIZED response, redirecting to login');

          // Clear user state
          if (typeof window !== 'undefined') {
            const { useUserStore } = await import('@/store/user/store');
            useUserStore.getState().setUser(undefined);
            useUserStore.getState().setAuthenticated(false);
            useUserStore.getState().setInitialized(false);
          }

          // Redirect to authorize (SSO login)
          if (typeof window !== 'undefined') {
            const { useUserStore } = await import('@/store/user/store');
            await useUserStore.getState().authorize();
          }
        }
      }

      throw new ApiError(
        data.message || data.error || 'An error occurred while fetching data',
        data.status || 'ERROR',
        data.error_code,
        response.status
      );
    }

    return data as T;
  };

  try {
    return await performFetch();
  } catch (error) {
    throw error;
  }
}

export async function fetcherServerSentEvent<T>(
  url: string,
  options: FetcherOptions = {}
): Promise<T> {
  const {
    simulateDelay = false,
    delayMs = 5000,
    // skipTokenRefresh = false,
    ...fetchOptions
  } = options;

  // Get selected workspace from store
  const selectedWorkspace = useWorkspaceStore.getState().selectedWorkspace;

  // Set default headers if not provided
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // Add workspace headers to ALL requests if workspace is selected
  if (selectedWorkspace) {
    headers['X-Workspace-Id'] = selectedWorkspace.workspace_id;
    headers['X-Organization-Id'] = selectedWorkspace.organization_id;
  }

  // Internal fetch function
  const performFetch = async (): Promise<T> => {
    // Mô phỏng delay nếu được yêu cầu
    if (simulateDelay) {
      await delay(delayMs);
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      credentials: 'include',
    });

    // Parse the JSON response
    const data = await response.json();

    // Handle API error responses
    if (!response.ok || data.status === 'INVALID' || data.status === 'ERROR') {
      // Handle UNAUTHORIZED - redirect to login
      if (data.status === 'UNAUTHORIZED') {
        // Check if we're not already on a public/auth page to avoid redirect loops
        const publicPaths = [
          '/login',
          '/signup',
          '/auth',
          '/callback',
          '/login-callback',
          '/error',
          '/403',
          '/unauthorized',
        ];
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

        // Check if user is currently logging out - skip redirect if so
        let isLoggingOut = false;
        if (typeof window !== 'undefined') {
          const { useUserStore } = await import('@/store/user/store');
          isLoggingOut = useUserStore.getState().isLoggingOut ?? false;
        }

        if (!isLoggingOut && !publicPaths.some(path => currentPath.startsWith(path))) {
          console.warn('[FetcherSSE] UNAUTHORIZED response, redirecting to login');

          // Clear user state
          if (typeof window !== 'undefined') {
            const { useUserStore } = await import('@/store/user/store');
            useUserStore.getState().setUser(undefined);
            useUserStore.getState().setAuthenticated(false);
            useUserStore.getState().setInitialized(false);
          }

          // Redirect to authorize (SSO login)
          if (typeof window !== 'undefined') {
            const { useUserStore } = await import('@/store/user/store');
            await useUserStore.getState().authorize();
          }
        }
      }

      throw new ApiError(
        data.message || data.error || 'An error occurred while fetching data',
        data.status || 'ERROR',
        data.error_code,
        response.status
      );
    }

    return data as T;
  };

  try {
    return await performFetch();
  } catch (error) {
    throw error;
  }
}
